<template>
  <FormCard
    :title="$t('order_creation.details_step.company_heading')"
    data-testid="order-details-company-card"
    :is-open="isOpen"
    :completed="completed"
    :has-error="hasError"
    :show-chevron="true"
    :show-form-icons-on-open="false"
    @toggle="emit('toggle')"
    @enter-pressed="emit('enter-pressed')">
    <template #description>
      <div v-if="!isOpen" class="company-summary">
        <div class="company-avatars">
          <template v-for="(node, idx) in companyChain" :key="idx">
            <img v-if="node.logoUrl" v-tooltip="node.name" :src="node.logoUrl" :alt="node.name" class="company-logo" />
            <div v-else v-tooltip="node.name" class="company-initials">{{ node.initials || '?' }}</div>
          </template>
        </div>
        <div class="company-text">
          <p v-tooltip="companyChainFullText" class="summary-title company-breadcrumb">
            <span v-if="immediateParentName" class="company-breadcrumb-parent">
              <span class="company-breadcrumb-parent-inner">{{ immediateParentName }}</span>
            </span>
            <span v-if="immediateParentName" class="company-breadcrumb-separator">&nbsp;/&nbsp;</span>
            <span class="company-breadcrumb-current">{{ currentCompanyName }}</span>
          </p>
          <p class="summary-subtitle">{{ companyAddressDisplay }}</p>
        </div>
      </div>
    </template>
    <template #form-content>
      <CompanySelector
        v-model:company-id="order.company_id"
        v-model:company-name="order.company_name"
        v-model:company-logo-image-id="logoImageId"
        data-testid="order-details-company-selector"
        :placeholder="$t('order_creation.details_step.company_placeholder')"
        :label="$t('order_creation.details_step.company_title')"
        :free-text-entry="true"
        :clearable="true"
        :errors="error ? [error] : []" />

      <OrderDetailsCompanyLogo
        v-if="(order.company_id || order.company_name) && !isHydrating && (!hasCompanyLogo || removeExistingLogo)"
        ref="logoRef"
        :existing-logo-url="companyLogoUrl"
        @file-selected="onLogoFileSelected"
        @remove="onLogoRemove" />

      <div class="form-actions">
        <LeadButton
          data-testid="order-details-company-save-button"
          variant="secondary"
          @click="handleSave"
          :loading="saving || logoUploading"
          >{{ $t('btn.save_and_continue') }}
          <template #icon>
            <IconCheck :size="16" stroke-width="2" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { IconCheck } from '@tabler/icons-vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import CompanySelector from '@/components/company/CompanySelector.vue';
import OrderDetailsCompanyLogo from './OrderDetailsCompanyLogo.vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { calculateInitials } from '@/helpers/helpers';
import { requestMultipart } from '@/api/client';
import { updateCompany } from '@/api/company';
import { changeOrderImages } from '@/api/order';
import type { ImageObject } from '@/types/types';

withDefaults(
  defineProps<{
    isOpen: boolean;
    completed: boolean;
    hasError: boolean;
    error?: string;
    saving?: boolean;
  }>(),
  {
    saving: false,
  }
);

const emit = defineEmits<{
  toggle: [];
  save: [];
  'enter-pressed': [];
}>();

const store = useOrderCreationStore();
const { order, orderImages } = storeToRefs(store);

const logoRef = ref<InstanceType<typeof OrderDetailsCompanyLogo> | null>(null);
const logoUploading = ref(false);
const pendingLogoFile = ref<File | null>(null);
const removeExistingLogo = ref(false);
const isCreatingNewCompany = ref(false);
const pendingLogoForNewCompany = ref<File | null>(null);
const isHydrating = ref(false);
const logoImageId = ref<number | undefined>(undefined);
// company the order draft is known to be stored with server-side. only ever set
// from a company the backend has already saved on the order, so a selection the
// user has not saved yet never counts as persisted (see handleSave).
const persistedCompanyId = ref<number | undefined>(order.value.company_id);

interface CompanyChainNode {
  name: string;
  logoUrl: string | null;
  initials: string;
}

const hasCompanyLogo = computed(() => {
  return !!logoImageId.value;
});

// the logo the company carries server-side, as last reported by hydrateCompanyHierarchy.
// undefined while the company has none, so it also answers "is there anything to remove".
const storedCompanyLogo = computed(() => order.value.company?.data?.logo_image?.data);

const companyLogoUrl = computed<string | undefined>(() => {
  if (removeExistingLogo.value) return undefined;

  const logo = storedCompanyLogo.value;
  if (!logo?.id) return undefined;

  const thumbnail = logo.proxy?.url_thumbnail;
  if (thumbnail) {
    try {
      new URL(thumbnail);
      return thumbnail;
    } catch {}
  }

  try {
    const url = new URL(`/v1/media/images/${logo.id}/jump`, window.location.origin);
    return url.toString();
  } catch {
    return undefined;
  }
});

const onLogoFileSelected = (file: File) => {
  pendingLogoFile.value = file;
  removeExistingLogo.value = false;
};

const onLogoRemove = () => {
  // a picked file is only uploaded on save, so discarding it beforehand is purely local:
  // the picker falls back to the stored logo and nothing needs detaching. removing with
  // nothing pending targets the stored logo, which handleSave then detaches.
  const discardedPendingFile = !!pendingLogoFile.value;
  pendingLogoFile.value = null;
  removeExistingLogo.value = !discardedPendingFile && !!storedCompanyLogo.value?.id;
};

const handleSave = async () => {
  if (!order.value.company_id) {
    isCreatingNewCompany.value = true;
    pendingLogoForNewCompany.value = pendingLogoFile.value;
    emit('save');
    return;
  }

  if (pendingLogoFile.value) {
    const file = pendingLogoFile.value;
    logoUploading.value = true;

    // the backend assigns the default company logo to the order's *persisted*
    // company, which is still the previously selected one while a company switch
    // is unsaved. save the switch first, otherwise the logo lands on the company
    // the user just switched away from (PJAS-4991).
    if (order.value.id && order.value.company_id !== persistedCompanyId.value) {
      try {
        await store.updateDraft();
        persistedCompanyId.value = order.value.company_id;
      } catch (error) {
        console.error('Failed to save the company before uploading the logo:', error);
        logoUploading.value = false;
        // hand over to the step's own save, so the failure surfaces on the card
        emit('save');
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await requestMultipart('POST', '/v1/account/media/images/upload', { class: 0 }, formData);
      const imageObject = res.data as unknown as ImageObject;

      if (order.value.id) {
        await changeOrderImages(
          order.value.id,
          {
            companyLogo: [imageObject],
            images: [],
          },
          { setDefaultCompanyLogo: true }
        );
      }

      await store.hydrateCompanyHierarchy();

      pendingLogoFile.value = null;
      removeExistingLogo.value = false;
      logoRef.value?.reset();
    } catch (error) {
      console.error('Failed to upload logo:', error);
      logoUploading.value = false;
      return;
    } finally {
      logoUploading.value = false;
    }
  } else if (removeExistingLogo.value) {
    try {
      await updateCompany(order.value.company_id, { ...(order.value.company?.data ?? {}), logo_image_id: null } as any);
      if (order.value.id) {
        await changeOrderImages(
          order.value.id,
          {
            companyLogo: [],
            images: [],
          },
          { setDefaultCompanyLogo: true }
        );
      }
      await store.hydrateCompanyHierarchy();
      removeExistingLogo.value = false;
    } catch (error) {
      console.error('Failed to remove logo:', error);
      return;
    }
  } else {
    // If the company already has a logo, auto-attach it to the order images.
    const logo = storedCompanyLogo.value;
    if (order.value.id && logo?.hash) {
      try {
        await changeOrderImages(order.value.id, {
          companyLogo: [logo as ImageObject],
          images: [],
        });
        orderImages.value.companyLogo = [logo as ImageObject];
      } catch (e) {
        console.error('Failed to auto-upload existing company logo:', e);
      }
    }
  }

  emit('save');
};

watch(
  () => order.value.company_id,
  async (newId) => {
    if (isCreatingNewCompany.value && newId && pendingLogoForNewCompany.value) {
      isCreatingNewCompany.value = false;
      // the step's save created this company and stored it on the order draft
      persistedCompanyId.value = newId;
      isHydrating.value = true;

      logoUploading.value = true;
      try {
        const formData = new FormData();
        formData.append('image', pendingLogoForNewCompany.value);

        const res = await requestMultipart('POST', '/v1/account/media/images/upload', { class: 0 }, formData);
        const imageObject = res.data as unknown as ImageObject;

        if (order.value.id) {
          await changeOrderImages(
            order.value.id,
            {
              companyLogo: [imageObject],
              images: [],
            },
            { setDefaultCompanyLogo: true }
          );
        }

        await store.hydrateCompanyHierarchy();

        pendingLogoForNewCompany.value = null;
        pendingLogoFile.value = null;
        removeExistingLogo.value = false;
        logoRef.value?.reset();
      } catch (error) {
        console.error('Failed to upload logo for new company:', error);
      } finally {
        logoUploading.value = false;
        isHydrating.value = false;
      }
      return;
    }

    isCreatingNewCompany.value = false;
    pendingLogoFile.value = null;
    removeExistingLogo.value = false;
    logoRef.value?.reset();

    if (newId) {
      isHydrating.value = true;
      try {
        await store.hydrateCompanyHierarchy();
      } finally {
        isHydrating.value = false;
      }
    } else {
      order.value.company = undefined as any;
      isHydrating.value = false;
    }
  },
  { immediate: true }
);

// Walks order.company.data.parent upward, returning the chain from root down to
// the current company (current is last). Each node has a resolved logo_image url
// (or null — caller renders initials instead).
const companyChain = computed<CompanyChainNode[]>(() => {
  const chain: CompanyChainNode[] = [];
  const toNode = (c: any): CompanyChainNode => {
    const name = c?.name || '';
    return {
      name,
      logoUrl: c?.logo_image?.data?.proxy?.url_thumbnail ?? null,
      initials: calculateInitials(name),
    };
  };

  const ancestors: any[] = [];
  let node: any = order.value.company?.data?.parent;
  while (node && typeof node === 'object' && !Array.isArray(node)) {
    ancestors.unshift(node);
    node = node.parent;
  }
  ancestors.forEach((a) => chain.push(toNode(a)));

  const current = order.value.company?.data;
  if (current?.id) {
    chain.push(toNode(current));
  } else if (order.value.company_name) {
    chain.push({
      name: order.value.company_name,
      logoUrl: null,
      initials: calculateInitials(order.value.company_name),
    });
  }

  return chain;
});

// Full path shown in the tooltip — every ancestor + current.
const companyChainFullText = computed(() =>
  companyChain.value
    .map((n) => n.name)
    .filter(Boolean)
    .join(' / ')
);

const currentCompanyName = computed(() => {
  const chain = companyChain.value;
  if (chain.length > 0) return chain[chain.length - 1].name;
  return order.value.company_name || '';
});

// Only the immediate parent is rendered in the breadcrumb; older ancestors are
// implicit in the left-side ellipsis on the parent name and visible via the
// line's tooltip.
const immediateParentName = computed(() => {
  const chain = companyChain.value;
  return chain.length >= 2 ? chain[chain.length - 2].name : '';
});

const companyAddressDisplay = computed(() => {
  const companyData = order.value.company?.data;
  const city = order.value.company_city || companyData?.address_city;
  const street = order.value.company_street || companyData?.address_street;
  const zip = order.value.company_zip || companyData?.address_zip;

  const parts: string[] = [];
  if (street) parts.push(street);
  const zipCity = [zip, city].filter(Boolean).join(' ');
  if (zipCity) parts.push(zipCity);

  return parts.join(', ') || '';
});
</script>

<style scoped lang="scss">
* {
  font-family: var(--font-brand);
}

.summary-title {
  font-weight: 400;
  font-size: 14px;
  color: #101828;
  margin: 0;
}

.summary-subtitle {
  font-size: 13px;
  color: #8e939d;
  margin: 0;
}

.company-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  // Reserve space for FormCard's absolutely-positioned chevron at right: 20px.
  padding-right: 32px;
}

.company-breadcrumb {
  display: flex;
  align-items: baseline;
  min-width: 0;
  max-width: 100%;
}

.company-breadcrumb-separator {
  flex: 0 0 auto;
  white-space: pre;
}

// Immediate parent. Uses the direction:rtl trick so text-overflow ellipsis
// collapses from the LEFT, keeping the tail of the name visible (e.g.
// `…Fachhochschule BFH` stays as `…BFH` when space is tight). The inner span
// flips direction back to LTR so the name renders correctly.
.company-breadcrumb-parent {
  flex: 0 100 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: rtl;
}

.company-breadcrumb-parent-inner {
  direction: ltr;
  unicode-bidi: bidi-override;
}

// Current company never shrinks — the whole point of this layout.
.company-breadcrumb-current {
  flex: 0 0 auto;
  white-space: nowrap;
}

.company-avatars {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.company-avatars > * + * {
  margin-left: -30px;
}

.company-text {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.company-text .summary-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-logo,
.company-initials {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  object-fit: contain;
  background-color: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  color: #475467;
  flex-shrink: 0;
  border: 2px solid #fff;
  box-sizing: content-box;
  position: relative;
}

// Bring the rightmost (current company) avatar to the front.
.company-avatars > :last-child {
  z-index: 2;
}

.company-avatars > :nth-last-child(2) {
  z-index: 1;
}

.form-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
}
</style>
